import React, { useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  formatWon,
  buildEstimatedClaimableBreakdown,
  failedTaxYearLabels,
  formatTaxYearLabels,
} from '../../lib/hiddenInsuranceMoneyUtils'
import { hiddenInsuranceReportPrintStyles } from '../../lib/hiddenInsuranceReportPrintStyles'
import { openCustomerCardNewsWindow } from '../../lib/customerCardNews'
import HiddenInsuranceClaimComparisonChart from './HiddenInsuranceClaimComparisonChart'

async function fetchReport(jobId, filters, sortBy) {
  const res = await api.get(`/medical-reimbursement-lookups/${jobId}/report`, {
    params: {
      generation: filters.generation,
      includeGlasses: !filters.excludeGlasses,
      excludePharmacyBelowThreshold: filters.excludePharmacyBelowThreshold,
      includeFamily: Boolean(filters.includeFamily),
      sortBy,
    },
  })
  return res.data?.data || res.data
}

function ReportMetric({ label, value, sub, compact, fillHeight, tight, className = '' }) {
  return (
    <div
      className={`rounded-lg border border-slate-200 bg-slate-50 ${compact ? (tight ? 'px-1.5 py-1.5' : 'px-2 py-1.5') : 'px-3 py-2.5'} ${fillHeight ? 'flex h-full min-h-full w-full flex-col justify-center' : ''} ${className}`}
    >
      <div className={`font-medium text-slate-500 ${compact ? (tight ? 'text-[8px] leading-tight' : 'text-[10px]') : 'text-[11px]'}`}>{label}</div>
      <div className={`font-bold tabular-nums text-slate-900 ${compact ? (tight ? 'mt-0.5 text-xs' : 'mt-0 text-sm') : 'mt-0.5 text-base'}`}>{value}</div>
      {sub ? (
        <div className={`text-slate-500 ${compact ? (tight ? 'mt-0.5 text-[8px] leading-tight' : 'mt-0 text-[9px]') : 'mt-0.5 text-[10px]'}`}>{sub}</div>
      ) : null}
    </div>
  )
}

function ReportBreakdownLine({ label, amount }) {
  return (
    <div className="hi-report-breakdown-line grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 text-[9px] leading-snug text-slate-600">
      <span className="break-keep">{label}</span>
      <span className="shrink-0 whitespace-nowrap text-right font-medium tabular-nums text-slate-700">{formatWon(amount)}</span>
    </div>
  )
}

function safeReportFileNamePart(value) {
  const cleaned = String(value || '').trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ')
  return cleaned || '고객'
}

function formatCount(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString('ko-KR')
}

function statPeriodLabel(release) {
  if (!release) return ''
  const name = release.releaseName || ''
  const period = release.periodStart && release.periodEnd
    ? `${release.periodStart}~${release.periodEnd}`
    : ''
  return [name, period].filter(Boolean).join(' · ')
}

function ReportDocument({ report, customerName, filters }) {
  const summary = report?.summary || {}
  const guide = summary.guide || {}
  const totals = summary.totals || {}
  const counts = summary.counts || {}
  const interpretation = report?.interpretation || {}
  const segments = report?.segments || []
  const segmentsMeta = report?.segmentsMeta || {}
  const highlights = report?.highlights || []
  const targetYears = summary.targetYears || []
  const statistics = report?.statistics || {}
  const statisticItems = statistics.items || []
  const statCoverageReview = report?.statCoverageReview || {}
  const statRecommendations = statCoverageReview.recommendations || []
  const coverageInsight = report?.coverageInsight || {}
  const coverageGroups = coverageInsight.topCoverageGroups || []
  const reviewHints = coverageInsight.reviewHints || []
  const claimRatio = guide.claimRatio
  const generationLabel = summary.generationLabel || '실손 3세대'
  const includeFamily = summary.filters?.includeFamily === true || filters?.includeFamily === true
  const personScopeNotice = includeFamily
    ? '현재 리포트는 가족 포함 필터가 켜져 있어 가족 의료비·실손 수령까지 함께 반영합니다.'
    : '현재 리포트는 대상자명이 고객명과 일치하는 본인 의료비·실손 수령만 반영합니다.'
  const failedYearLabels = failedTaxYearLabels(summary.yearStatuses, summary.failedYears)
  const successfulYearLabels = formatTaxYearLabels(summary.successfulYears)
  const claimableBreakdown = summary.claimableBreakdown?.lines
    ? summary.claimableBreakdown
    : buildEstimatedClaimableBreakdown({ guide, filters: filters || {}, totals })

  return (
    <div className="hidden-insurance-report-doc mx-auto max-w-[720px] bg-white text-slate-900">
      <header className="flex items-start justify-between gap-3 border-b-2 border-brand-navy pb-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wide text-brand-navy">숨은보험금 점검 요약</div>
          <h1 className="mt-0.5 text-lg font-bold text-slate-900">
            {customerName || report?.customerName || '고객'}님
          </h1>
          <p className="mt-0.5 text-xs text-slate-600">
            국세청 연말정산 간소화 ({targetYears.join(', ')}년) · 조회일 {report?.reportDate || '-'}
          </p>
          <p className="text-[10px] text-slate-500">{summary.filtersLabel}</p>
        </div>
        <div className="shrink-0 rounded-xl border border-brand-navy bg-slate-50 px-4 py-3 text-right" style={{ minWidth: 120 }}>
          <div className="text-xs font-medium text-slate-500">숨은보험금 가능성</div>
          <div className="mt-1.5 whitespace-nowrap text-2xl font-extrabold leading-none text-brand-navy">
            {summary.possibilityLabel || '-'}
          </div>
        </div>
      </header>

      <section className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <h2 className="text-xs font-semibold text-slate-800">자료 해석 기준</h2>
        <p className="mt-1 text-[10px] leading-snug text-slate-600">
          숨은보험금 가능성은 국세청 의료비 공제자료 기준의 참고 데이터입니다. {personScopeNotice}
          {' '}가족의 카드나 가족 연말정산 자료에 잡힌 의료비는 고객 기준 조회에서 보이지 않을 수 있습니다.
        </p>
        {failedYearLabels.length > 0 ? (
          <p className="mt-1 rounded border border-amber-100 bg-amber-50 px-2 py-1 text-[10px] leading-snug text-amber-900">
            일부 연도 자료는 조회 실패로 계산에서 제외되었습니다.
            {successfulYearLabels.length > 0 ? ` 성공 연도: ${successfulYearLabels.join(', ')}.` : ''}
            {' '}제외 연도: {failedYearLabels.join(', ')}.
          </p>
        ) : null}
      </section>

      <section className="mt-3">
        <h2 className="text-sm font-semibold text-slate-800">3개년 현황</h2>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          <ReportMetric
            compact
            label="의료비 합계"
            value={formatWon(totals.medicalExpense)}
            sub={`진료·약국 ${totals.visitCount ?? counts.medicalFiltered ?? 0}건`}
          />
          <ReportMetric
            compact
            label="실손 수령 합계"
            value={formatWon(totals.reimbursement)}
            sub={`${counts.reimbursements ?? 0}건`}
          />
          <ReportMetric
            compact
            label="보험료 납입 합계"
            value={formatWon(totals.insurancePremium)}
            sub={`${counts.premiums ?? 0}건`}
          />
        </div>
      </section>

      <section className="mt-3 rounded-xl border border-slate-200 px-3 py-2">
        <div
          className="hi-report-claim-summary-grid grid items-stretch gap-1.5"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(84px, 104px) minmax(64px, 80px) minmax(96px, 112px)' }}
        >
          <div className="flex min-h-full flex-col rounded-lg border border-slate-200 bg-white px-2 py-2">
            <div className="text-[9px] font-medium text-slate-500">추정 청구 가능액</div>
            <div className="mt-0.5 text-sm font-bold tabular-nums leading-tight text-slate-900">
              {formatWon(guide.estimatedClaimable)}
            </div>
            {claimableBreakdown.lines?.length > 0 ? (
              <div className="mt-2 border-t border-slate-100 pt-1.5">
                <div className="text-[9px] font-medium text-slate-500">제외·공제 내역</div>
                <div className="mt-1 space-y-1">
                  {claimableBreakdown.lines.map((line) => (
                    <ReportBreakdownLine key={line.key} label={line.label} amount={line.amount} />
                  ))}
                  {claimableBreakdown.totalReduction > 0 ? (
                    <div className="hi-report-breakdown-line grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 border-t border-slate-100 pt-1 text-[9px] font-medium text-slate-700">
                      <span>합계</span>
                      <span className="shrink-0 whitespace-nowrap text-right tabular-nums">{formatWon(claimableBreakdown.totalReduction)}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <ReportMetric compact fillHeight label="실손 수령" value={formatWon(guide.reimbursementTotal)} />
          <ReportMetric
            compact
            fillHeight
            tight
            label="수령 비율"
            value={claimRatio != null ? `${claimRatio}%` : '-'}
            sub={generationLabel.replace('실손 ', '')}
          />
          <ReportMetric compact fillHeight label="미청구 여지" value={formatWon(guide.unclaimedHint)} />
        </div>
      </section>

      <section className="mt-2 rounded-xl border border-slate-200 px-3 py-2">
        <h2 className="text-xs font-semibold text-slate-700">추정 청구 vs 실손 수령</h2>
        <HiddenInsuranceClaimComparisonChart
          compact
          slim
          estimatedClaimable={guide.estimatedClaimable}
          reimbursementTotal={guide.reimbursementTotal}
          claimRatio={claimRatio}
          unclaimedHint={guide.unclaimedHint}
        />
      </section>

      <section className="mt-3 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2">
        <h2 className="text-sm font-semibold text-amber-950">{interpretation.headline}</h2>
        <p className="mt-0.5 text-[11px] leading-snug text-amber-900">{interpretation.body}</p>
      </section>

      <section className="mt-3">
        <h2 className="text-sm font-semibold text-slate-800">왜 지금 점검이 필요한가요</h2>
        <ul className="mt-1 list-inside list-disc space-y-0.5 text-[11px] leading-snug text-slate-600">
          {(report?.whyCheck || []).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="mt-3 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2">
        <div className="flex flex-wrap items-baseline justify-between gap-1">
          <h2 className="text-sm font-semibold text-slate-800">진료 통계 비교</h2>
          <span className="text-[9px] text-slate-500">{statPeriodLabel(statistics.release)}</span>
        </div>
        {statistics.status === 'READY' && statisticItems.length > 0 ? (
          <div className="mt-1.5 space-y-1">
            {statisticItems.slice(0, 3).map((item) => (
              <div key={`${item.kcdCode3}-${item.visitType}-${item.providerType}`} className="rounded border border-sky-100 bg-white px-2 py-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-[11px] font-semibold text-slate-800">
                      {item.kcdCode3} · {item.diseaseName}
                    </div>
                    <div className="text-[9px] text-slate-500">
                      {item.providerTypeLabel} {item.visitTypeLabel} · 전국 {item.nationalRank ? `${item.nationalRank}위` : '-'} · 환자 {formatCount(item.nationalPatientCount)}명
                    </div>
                  </div>
                  <div className="shrink-0 text-right text-[9px] font-medium text-brand-navy">{item.comparisonLabel}</div>
                </div>
                <div className="mt-1 grid grid-cols-3 gap-1">
                  <ReportMetric compact tight label="내 진료비" value={formatWon(item.myTotalCost)} sub={`${formatCount(item.myEpisodeCount)}건`} />
                  <ReportMetric compact tight label="전국 평균" value={formatWon(item.nationalAvgCost)} sub="환자 1인당" />
                  <ReportMetric compact tight label="전국 총진료비" value={formatWon(item.nationalTotalCost)} />
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[9px] leading-tight">
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700">
                    {item.priorityLabel || '참고'}
                  </span>
                  <span className="rounded-full bg-sky-100 px-1.5 py-0.5 font-medium text-sky-900">
                    {item.actionLabel || item.coverageMatch?.label || '담보 점검'}
                  </span>
                  {item.coverageMatch?.matchedCount > 0 ? (
                    <span className="truncate text-slate-500">
                      {item.coverageMatch.items?.slice(0, 2).map((coverage) => coverage.coverageName).filter(Boolean).join(', ')}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
            {statRecommendations.length > 0 ? (
              <ul className="rounded border border-sky-100 bg-white px-2 py-1.5 text-[9px] leading-snug text-slate-600">
                {statRecommendations.slice(0, 2).map((line) => (
                  <li key={line}>• {line}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <p className="mt-1 text-[11px] leading-snug text-slate-600">
            {statistics.message || '활성화된 통계 또는 매칭 가능한 진료정보가 없어 통계 비교를 표시하지 않았습니다.'}
          </p>
        )}
      </section>

      <section className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2">
        <h2 className="text-sm font-semibold text-slate-800">계약·담보 연결 요약</h2>
        <div className="mt-1.5 grid grid-cols-3 gap-1.5">
          <ReportMetric compact tight label="계약" value={`${formatCount(coverageInsight.contractCount)}건`} />
          <ReportMetric compact tight label="담보" value={`${formatCount(coverageInsight.coverageCount)}개`} />
          <ReportMetric compact tight label="월 보험료" value={formatWon(coverageInsight.monthlyPremium)} />
        </div>
        {coverageInsight.statLinkedDiseaseCount != null || coverageInsight.statUncoveredDiseaseCount != null ? (
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            <ReportMetric compact tight label="통계 연결 담보" value={`${formatCount(coverageInsight.statLinkedCoverageCount)}개`} />
            <ReportMetric compact tight label="담보 점검 질환" value={`${formatCount(coverageInsight.statUncoveredDiseaseCount)}개`} />
          </div>
        ) : null}
        {coverageGroups.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {coverageGroups.slice(0, 6).map((group) => (
              <span key={group.name} className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[9px] font-medium text-emerald-900">
                {group.name} {formatCount(group.count)}
              </span>
            ))}
          </div>
        ) : null}
        {reviewHints.length > 0 ? (
          <ul className="mt-2 list-inside list-disc space-y-0.5 text-[10px] leading-snug text-emerald-950">
            {reviewHints.slice(0, 3).map((hint) => (
              <li key={hint}>{hint}</li>
            ))}
          </ul>
        ) : null}
      </section>

      {highlights.length > 0 ? (
        <section className="mt-3">
          <h2 className="text-sm font-semibold text-slate-800">주목 진료 (고액 상위)</h2>
          <ul className="mt-1 space-y-1">
            {highlights.map((item, idx) => (
              <li key={`${item.title}-${idx}`} className="flex items-center justify-between gap-2 rounded border border-slate-100 px-2 py-1.5 text-[11px]">
                <div className="min-w-0">
                  <div className="font-medium text-slate-800">{item.title}</div>
                  <div className="truncate text-slate-500">{item.subtitle}</div>
                </div>
                <div className="shrink-0 font-semibold text-slate-900">{formatWon(item.amount)}</div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-3">
        <h2 className="text-sm font-semibold text-slate-800">실손 수령일 기준 구간 비교</h2>
        {segmentsMeta.skipped || segmentsMeta.skipReason ? (
          <p className="mt-1 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
            {segmentsMeta.skipReason || '구간별 비교를 표시하지 않았습니다.'}
          </p>
        ) : null}
        {segments.length > 0 ? (
          <div className="mt-1 space-y-1.5">
            {segments.map((seg) => (
              <div key={`${seg.receiptDateLabel}-${seg.periodLabel}`} className="rounded-lg border border-slate-200 px-2 py-2">
                <div className="flex flex-wrap items-baseline justify-between gap-1">
                  <div className="text-[11px] font-semibold text-brand-navy">
                    실손 수령 {seg.receiptDateLabel}
                    {seg.receiptSubtitle ? ` · ${seg.receiptSubtitle}` : ''}
                  </div>
                  <div className="text-[9px] text-slate-500">진료 구간 {seg.periodLabel}</div>
                </div>
                <div className="mt-1 grid grid-cols-2 gap-1 sm:grid-cols-5">
                  <ReportMetric compact label="진료 합계" value={formatWon(seg.medicalTotal)} sub={`${seg.visitCount}건`} />
                  <ReportMetric compact label="추정 청구" value={formatWon(seg.estimatedClaimable)} />
                  <ReportMetric compact label="실손 수령" value={formatWon(seg.reimbursementAmount)} />
                  <ReportMetric compact label="수령 비율" value={seg.claimRatio != null ? `${seg.claimRatio}%` : '-'} />
                  <ReportMetric compact label="미청구 여지" value={formatWon(seg.unclaimedHint)} />
                </div>
              </div>
            ))}
            {segmentsMeta.totalReceiptCount > segmentsMeta.shownCount ? (
              <p className="text-[10px] text-slate-500">
                최근 {segmentsMeta.shownCount}건 구간만 표시 (전체 수령 {segmentsMeta.totalReceiptCount}건)
              </p>
            ) : null}
          </div>
        ) : !segmentsMeta.skipReason ? (
          <p className="mt-2 text-xs text-slate-500">표시할 구간 비교가 없습니다.</p>
        ) : null}
      </section>

      <footer className="mt-3 border-t border-slate-200 pt-2">
        <p className="text-[9px] leading-snug text-slate-500">{report?.disclaimer}</p>
        <p className="mt-1 text-[11px] font-medium text-slate-700">다음 단계: 보장·계약 정리 · 실손 청구 점검 상담</p>
      </footer>
    </div>
  )
}

export default function HiddenInsuranceReportModal({
  open,
  onClose,
  jobId,
  customerName,
  filters,
  sortBy,
  showCardNews = false,
  showShareImageButton = false,
  canDownloadShareImage = false,
  onDownloadShareImage,
  shareImageDownloading = false,
}) {
  const printRef = useRef(null)

  const reportQuery = useQuery({
    queryKey: ['hidden-insurance-report', jobId, filters, sortBy],
    queryFn: () => fetchReport(jobId, filters, sortBy),
    enabled: open && jobId != null,
    staleTime: 5 * 60 * 1000,
  })

  const handleDownload = useCallback(() => {
    const node = printRef.current
    if (!node) return

    const title = `${safeReportFileNamePart(customerName || reportQuery.data?.customerName)}_숨은보험금찾기`
    const html = `<!DOCTYPE html><html lang="ko"><head><meta charset="utf-8" /><title>${title}</title><style>${hiddenInsuranceReportPrintStyles}</style></head><body>${node.innerHTML}</body></html>`

    const iframe = document.createElement('iframe')
    iframe.setAttribute('title', title)
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    document.body.appendChild(iframe)

    const win = iframe.contentWindow
    if (!win) {
      iframe.remove()
      return
    }

    const doc = win.document
    doc.open()
    doc.write(html)
    doc.close()

    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
      }
    }

    const triggerPrint = () => {
      try {
        win.focus()
        win.print()
      } finally {
        win.onafterprint = cleanup
        setTimeout(cleanup, 2000)
      }
    }

    // document.write 직후 onload가 이미 지나간 경우가 많아 setTimeout으로 인쇄 호출
    setTimeout(triggerPrint, 300)
  }, [customerName, reportQuery.data?.customerName])

  const handleCardNews = useCallback(() => {
    const report = reportQuery.data || {}
    const guide = report?.summary?.guide || {}
    const opened = openCustomerCardNewsWindow({
      sourceType: 'hidden',
      customer: { customerName: customerName || report.customerName },
      latestHiddenInsurance: {
        id: jobId,
        status: 'COMPLETED',
        createdAt: report.createdAt,
        createdAtDisplay: report.createdAtDisplay || report.reportDate,
        estimatedClaimable: guide.estimatedClaimable,
        reimbursementTotal: guide.reimbursementTotal,
        unclaimedHint: guide.unclaimedHint,
      },
    })
    if (!opened) {
      window.alert('팝업 차단을 해제한 뒤 다시 시도해주세요.')
    }
  }, [customerName, jobId, reportQuery.data])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <div className="font-semibold text-slate-900">숨은보험금 점검 리포트</div>
          </div>
          <div className="flex items-center gap-2">
            {showShareImageButton ? (
              <button
                type="button"
                className="rounded border border-brand-navy bg-brand-navy px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onDownloadShareImage}
                disabled={!canDownloadShareImage || !reportQuery.data || reportQuery.isLoading || shareImageDownloading}
                title={canDownloadShareImage ? undefined : '마이페이지에서 리포트용 사진을 등록해 주세요.'}
              >
                {shareImageDownloading ? '생성 중...' : '발송 이미지'}
              </button>
            ) : null}
            {showCardNews ? (
              <button
                type="button"
                className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                onClick={handleCardNews}
                disabled={!reportQuery.data || reportQuery.isLoading}
              >
                카드뉴스
              </button>
            ) : null}
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={handleDownload}
              disabled={!reportQuery.data || reportQuery.isLoading}
            >
              다운로드
            </button>
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50"
              onClick={onClose}
            >
              닫기
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {reportQuery.isLoading ? (
            <div className="py-16 text-center text-sm text-slate-500">리포트를 준비하는 중…</div>
          ) : reportQuery.isError ? (
            <div className="py-16 text-center text-sm text-rose-700">리포트를 불러오지 못했습니다.</div>
          ) : (
            <div ref={printRef}>
              <ReportDocument report={reportQuery.data} customerName={customerName} filters={filters} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
